import { StatusCode } from "@/components/ui/status-code";
import {
  getHttpStatusCode,
  getMcpMethodNameFromAttributes,
  getMcpResourceName,
  getMcpToolNameFromAttributes,
  getTraceTransportType,
} from "@/utils/trace-utils";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { DurationCell } from "./DurationCell";
import { StatusCell } from "./StatusCell";
import type { ColumnMeta, EnhancedTrace } from "./types";

export const createTraceTableColumns = (): (ColumnDef<EnhancedTrace> & {
  meta: ColumnMeta;
})[] => [
  {
    id: "expand",
    header: "",
    cell: ({ row }: { row: Row<EnhancedTrace> }) => {
      const isExpanded = row.getIsExpanded();
      return (
        <div className="flex items-center justify-center w-8 h-8 hover:bg-muted/50 group-hover:bg-muted/70 rounded transition-colors  text-muted-foreground group-hover:text-foreground">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>
      );
    },
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableColumnFilter: false,
    enableSorting: false,
    meta: {
      priority: 0, // Always show expand column
      cellClassName: "px-0 flex justify-center",
    },
  },
  {
    accessorKey: "hasErrors",
    header: "Status",
    cell: ({ row }) => {
      const hasErrors = row.getValue("hasErrors") as boolean;
      const rootSpan = row.original.spans?.find((span) => span.isRootSpan);
      const httpStatus = rootSpan ? getHttpStatusCode(rootSpan) : null;

      // Prioritize showing MCP/operation errors over HTTP 200 success
      // Show StatusCell (OK/ERROR) when there are errors, regardless of HTTP status
      if (hasErrors) {
        return <StatusCell hasErrors={hasErrors} />;
      }

      // Show HTTP status code only when there are no MCP errors
      if (httpStatus !== null) {
        return (
          <div className="@container w-full">
            <div className="flex items-center min-w-0 w-full justify-center @[50px]:justify-start @[50px]:gap-1">
              <StatusCode
                statusCode={httpStatus}
                className="text-xs font-medium"
              />
            </div>
          </div>
        );
      }

      // Fallback to StatusCell for cases without HTTP status
      return <StatusCell hasErrors={hasErrors} />;
    },
    meta: {
      priority: 1,
    },
  },
  {
    accessorFn: (row) => {
      const transportType = getTraceTransportType(row);
      const rootSpan = row.spans?.find((span) => span.isRootSpan);

      if (transportType === "http-rpc" && rootSpan) {
        return getMcpMethodNameFromAttributes(rootSpan) || "unknown";
      }

      if (transportType === "sse") {
        return "connection"; // Or extract SSE-specific method
      }

      return "generic";
    },
    header: "Request",
    cell: ({ getValue }) => {
      const method = getValue() as string;

      return (
        <div className="text-xs font-mono text-muted-foreground break-words max-w-full">
          {method}
        </div>
      );
    },
    meta: {
      priority: 1,
    },
  },
  {
    accessorFn: (row) => {
      const transportType = getTraceTransportType(row);
      const rootSpan = row.spans?.find((span) => span.isRootSpan);

      if (transportType === "http-rpc" && rootSpan) {
        return (
          getMcpToolNameFromAttributes(rootSpan) ?? getMcpResourceName(rootSpan)
        );
      }

      if (transportType === "sse" && rootSpan) {
        // For SSE, could show event count or client count
        return rootSpan?.attributes?.["sse.events.sent"] || null;
      }

      return null;
    },
    header: "Target",
    cell: ({ getValue, row }) => {
      const resource = getValue() as string | number | null;
      const transportType = getTraceTransportType(row.original);

      if (!resource) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      if (transportType === "http-rpc") {
        return (
          <span
            className="text-xs font-mono text-muted-foreground break-words max-w-full"
            title={resource as string}
          >
            {resource}
          </span>
        );
      }

      if (transportType === "sse") {
        return (
          <span className="text-xs text-muted-foreground">
            {resource} events
          </span>
        );
      }

      return <span className="text-xs text-muted-foreground">{resource}</span>;
    },
    meta: {
      priority: 1,
    },
  },
  {
    accessorKey: "duration",
    header: "Duration",
    cell: ({ row, table }) => {
      const duration = row.getValue("duration") as number;
      const allDurations = table
        .getCoreRowModel()
        .rows.map((r) => r.getValue("duration") as number);
      const maxDuration = Math.max(...allDurations);

      return (
        <DurationCell
          duration={duration}
          maxDuration={maxDuration}
          trace={row.original}
        />
      );
    },
    meta: {
      priority: 2,
    },
  },
  {
    accessorKey: "startTime",
    header: () => <>UTC Time</>,
    cell: ({ row }) => {
      const date = new Date(row.getValue("startTime"));
      return (
        <div className="text-xs font-mono text-left pr-1">
          <div>
            {date.toLocaleTimeString("en-US", {
              timeZone: "UTC",
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              fractionalSecondDigits: 3,
            })}
          </div>
          <div className="text-muted-foreground">
            {date.toLocaleDateString("en-US", { timeZone: "UTC" })}
          </div>
        </div>
      );
    },
    enableResizing: false,
    meta: {
      priority: 1,
      cellClassName: "pr-1",
    },
  },
];
